import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { publicJobs, publicJob, applyToJob, myApplications, getJobScreening, submitScreening } from "@/lib/companyApi";
import { listMyInterviews } from "@/lib/schedulingApi";
import ScreeningBreakdown from "@/components/wishire/ScreeningBreakdown";
import {
  ArrowLeft,
  Briefcase,
  MapPin,
  IndianRupee,
  Search,
  CheckCircle2,
  Loader2,
  CalendarClock,
  Video,
  ExternalLink,
  Clock,
  Sparkles,
  Lock,
} from "lucide-react";
import PageLoader from "@/components/wishire/PageLoader";


const STAGE_LABEL = {
  applied: "Applied",
  screening: "In screening",
  interview: "Interview",
  offer: "Offer",
  hired: "Hired 🎉",
  rejected: "Closed",
};

export function JobsBrowse() {
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (query) => {
    setLoading(true);
    try {
      const { data } = await publicJobs(query);
      setJobs(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load("");
  }, []);

  return (
    <div className="container-x py-10 md:py-14">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
        <div>
          <span className="overline">Open roles</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
            Jobs that match{" "}
            <span className="italic text-stone-500">your Wishire Score.</span>
          </h1>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            load(q);
          }}
          className="flex items-center gap-2 max-w-md w-full"
        >
          <div className="relative flex-1">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              type="text"
              data-testid="jb-search"
              placeholder="Search role, skill or company"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white border border-stone-300 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 text-sm"
            />
          </div>
          <button data-testid="jb-search-submit" className="btn-primary text-sm" type="submit">
            Search
          </button>
        </form>
      </div>

      {loading ? (
        <p className="text-stone-500">Loading…</p>
      ) : jobs.length === 0 ? (
        <p className="text-stone-500">No open jobs match. Try a different query.</p>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {jobs.map((j) => (
            <li key={j.job_id} data-testid={`jb-card-${j.job_id}`}>
              <Link
                to={`/app/jobs/${j.job_id}`}
                className="block rounded-3xl border border-stone-200 bg-white p-6 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(28,25,23,0.25)] transition-all duration-300"
              >
                <p className="overline">{j.company_name}</p>
                <h3 className="font-display text-2xl mt-2 leading-tight">{j.title}</h3>
                <p className="text-stone-500 text-sm mt-1">{j.target_role}</p>
                <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-stone-700">
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={14} /> {j.location || (j.remote ? "Remote" : "—")}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Briefcase size={14} /> {j.employment_type?.replace("_", " ")}
                  </span>
                  {j.comp_min && j.comp_max && (
                    <span className="inline-flex items-center gap-1">
                      <IndianRupee size={14} /> {(j.comp_min / 100000).toFixed(1)}L – {(j.comp_max / 100000).toFixed(1)}L
                    </span>
                  )}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function JobApply() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [error, setError] = useState(null);

  // Screening interview state
  const [screening, setScreening] = useState(null);   // { required, questions, min_score, result }
  const [answers, setAnswers] = useState([]);
  const [scoring, setScoring] = useState(false);
  const [scResult, setScResult] = useState(null);      // { score, passed, min_score, verdict, summary }

  // Proctoring signals (recorded only while the screening is being answered)
  const proctor = useRef({ paste_attempts: 0, tab_switches: 0, blur_count: 0, started: null });
  const [pasteBlocked, setPasteBlocked] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicJob(id);
        setJob(data);
        if (data.screening_enabled) {
          const { data: sc } = await getJobScreening(id);
          setScreening(sc);
          setAnswers(new Array((sc.questions || []).length).fill(""));
          if (sc.result) setScResult({ score: sc.result.score, passed: sc.result.passed, min_score: sc.min_score, verdict: sc.result.verdict, summary: sc.result.summary });
        }
      } catch (e) {
        setError("Job not found");
      }
    })();
  }, [id]);

  // Tab-switch / focus monitoring — active only while answering an un-submitted screening
  useEffect(() => {
    if (!job?.screening_enabled || scResult) return;
    proctor.current.started = proctor.current.started || Date.now();
    const onVis = () => { if (document.hidden) proctor.current.tab_switches += 1; };
    const onBlur = () => { proctor.current.blur_count += 1; };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlur);
    return () => { document.removeEventListener("visibilitychange", onVis); window.removeEventListener("blur", onBlur); };
  }, [job, scResult]);

  const onAnswerPaste = (e) => {
    e.preventDefault();                       // block pasting (e.g. AI answers)
    proctor.current.paste_attempts += 1;
    setPasteBlocked(true);
    setTimeout(() => setPasteBlocked(false), 2600);
  };

  const submitScreen = async () => {
    setScoring(true); setError(null);
    const p = proctor.current;
    const payload = {
      paste_attempts: p.paste_attempts,
      tab_switches: p.tab_switches,
      blur_count: p.blur_count,
      total_seconds: p.started ? Math.round((Date.now() - p.started) / 1000) : 0,
    };
    try {
      const { data } = await submitScreening(id, answers, payload);
      setScResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not score your answers. Try again.");
    } finally { setScoring(false); }
  };

  const apply = async () => {
    setApplying(true);
    setError(null);
    try {
      const { data } = await applyToJob(id);
      setApplied(true);
      if (data.already_applied) {
        // okay — still mark success
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not apply");
    } finally {
      setApplying(false);
    }
  };

  if (!job)
    return (
      <div className="container-x py-10 text-stone-500">
        {error || "Loading…"}
      </div>
    );

  return (
    <div className="container-x py-10 md:py-14 max-w-4xl">
      <button
        onClick={() => navigate("/app/jobs")}
        className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900 mb-6"
      >
        <ArrowLeft size={14} /> All jobs
      </button>

      <p className="overline">{job.company_name}</p>
      <h1 className="font-display text-4xl md:text-5xl leading-tight mt-3" data-testid="jb-detail-title">
        {job.title}
      </h1>
      <p className="text-stone-500 mt-2">
        {job.target_role} · {job.location || (job.remote ? "Remote" : "—")} · {job.employment_type?.replace("_", " ")}
      </p>

      <div className="mt-8 rounded-3xl bg-white border border-stone-200 p-7">
        <p className="overline">About this role</p>
        <pre className="mt-3 whitespace-pre-wrap font-sans text-stone-800 leading-relaxed">{job.description}</pre>
        {job.requirements?.length > 0 && (
          <>
            <p className="overline mt-7">Must-haves</p>
            <ul className="mt-3 space-y-1.5 text-stone-800">
              {job.requirements.map((r, i) => (
                <li key={i}>• {r}</li>
              ))}
            </ul>
          </>
        )}
        {job.skills?.length > 0 && (
          <>
            <p className="overline mt-7">Skills</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {job.skills.map((s, i) => (
                <span key={i} className="px-3 py-1 rounded-full bg-stone-100 border border-stone-200 text-sm">{s}</span>
              ))}
            </div>
          </>
        )}
      </div>

      {/* ── Pre-apply screening interview ── */}
      {job.screening_enabled && !applied && (() => {
        const passed = scResult?.passed;
        const failed = scResult && !scResult.passed;
        return (
          <div className="mt-8 rounded-3xl border-2 border-[#C2410C]/30 bg-[#fff7ed] p-6 md:p-8" data-testid="jb-screening">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={16} className="text-[#C2410C]" />
              <span className="overline text-[#C2410C]">Pre-apply screening</span>
            </div>
            <h2 className="font-display text-2xl">A quick interview before you apply.</h2>
            <p className="text-stone-600 text-sm mt-1 leading-relaxed">
              {screening?.min_score
                ? `Answer these ${screening?.questions?.length || ""} questions personalized to this role. You need ${screening.min_score}+ to apply.`
                : `Answer these ${screening?.questions?.length || ""} questions personalized to this role to unlock your application.`}
            </p>

            {scResult ? (
              <div className={`mt-5 rounded-2xl p-5 ${passed ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`} data-testid="jb-screening-result">
                <div className="flex items-center gap-3">
                  <span className={`font-display text-3xl ${passed ? "text-emerald-700" : "text-red-600"}`}>{scResult.score}/100</span>
                  <span className={`text-sm font-medium ${passed ? "text-emerald-700" : "text-red-600"}`}>
                    {passed ? "Passed — you can apply below." : `Below the ${scResult.min_score} bar.`}
                  </span>
                </div>
                {scResult.breakdown && <ScreeningBreakdown breakdown={scResult.breakdown} />}
                {scResult.summary && <p className="text-sm text-stone-600 mt-3 leading-relaxed">{scResult.summary}</p>}
                {failed && (
                  <button onClick={() => setScResult(null)} className="mt-3 text-sm text-[#C2410C] hover:underline">Retake the screening →</button>
                )}
              </div>
            ) : (
              <div className="mt-5 space-y-4">
                {/* Monitored notice */}
                <div className="flex items-start gap-2 rounded-xl bg-stone-900 text-stone-100 px-4 py-3" data-testid="jb-proctor-notice">
                  <Lock size={15} className="text-amber-300 shrink-0 mt-0.5" />
                  <p className="text-xs leading-relaxed">
                    <b>This screening is monitored for integrity.</b> Paste is disabled, tab-switches are recorded, and answers are checked for authenticity. Answer in your own words.
                  </p>
                </div>
                {pasteBlocked && (
                  <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="jb-paste-blocked">
                    Pasting is disabled in this screening — please type your answer.
                  </p>
                )}
                {(screening?.questions || []).map((q, i) => (
                  <div key={i}>
                    <label className="block text-sm font-medium text-stone-800 mb-1.5">{i + 1}. {q}</label>
                    <textarea
                      rows={2}
                      data-testid={`jb-screening-q${i}`}
                      value={answers[i] || ""}
                      onChange={(e) => setAnswers((a) => a.map((v, idx) => (idx === i ? e.target.value : v)))}
                      onPaste={onAnswerPaste}
                      onDrop={(e) => e.preventDefault()}
                      placeholder="Type your answer…"
                      className="w-full px-4 py-2.5 rounded-xl bg-white border border-stone-300 text-sm focus:outline-none focus:border-stone-900"
                    />
                  </div>
                ))}
                <button
                  onClick={submitScreen}
                  disabled={scoring || answers.every((a) => !a.trim())}
                  data-testid="jb-screening-submit"
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-stone-900 text-white text-sm font-medium hover:bg-[#C2410C] transition-colors disabled:opacity-60"
                >
                  {scoring ? <><Loader2 size={15} className="animate-spin" /> Scoring…</> : <>Submit screening <Sparkles size={15} /></>}
                </button>
              </div>
            )}
          </div>
        );
      })()}

      <div className="mt-8 sticky bottom-4">
        <div className="rounded-3xl bg-stone-900 text-stone-50 p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 wsh-grain">
          <div>
            <p className="font-display text-2xl">Apply with your Wishire Score</p>
            <p className="text-stone-400 text-sm">The recruiter sees your composite score, resume signals & interview verdict.</p>
          </div>
          {applied ? (
            <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-emerald-300 text-stone-900 font-medium" data-testid="jb-applied">
              <CheckCircle2 size={16} /> Applied
            </span>
          ) : job.screening_enabled && !scResult?.passed ? (
            <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-stone-700 text-stone-300 font-medium cursor-not-allowed" data-testid="jb-apply-locked">
              <Lock size={15} /> Pass screening to apply
            </span>
          ) : (
            <button
              onClick={apply}
              disabled={applying}
              data-testid="jb-apply"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-300 text-stone-900 hover:bg-amber-200 transition-colors font-medium disabled:opacity-60"
            >
              {applying ? <><Loader2 size={16} className="animate-spin" /> Applying</> : "Apply now"}
            </button>
          )}
        </div>
        {error && <p className="mt-3 text-red-300 text-sm">{error}</p>}
      </div>
    </div>
  );
}

export function MyApplications() {
  const [apps, setApps] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    myApplications().then(r => setApps(r.data || [])).catch(() => setApps([])).finally(() => setLoading(false));
    listMyInterviews().then(r => setInterviews(r.data || [])).catch(() => setInterviews([]));
  }, []);

  const activeInterviews = interviews.filter(
    (i) => ["invited", "booked", "rescheduled"].includes(i.status)
  );

  const STAGES = [
    { id: "applied", label: "Applied", color: "border-blue-200 bg-blue-50" },
    { id: "screening", label: "Screening", color: "border-amber-200 bg-amber-50" },
    { id: "interview", label: "Interview", color: "border-purple-200 bg-purple-50" },
    { id: "offer", label: "Offer", color: "border-emerald-200 bg-emerald-50" },
    { id: "hired", label: "Hired 🎉", color: "border-emerald-300 bg-emerald-100" },
    { id: "rejected", label: "Closed", color: "border-stone-200 bg-stone-100" },
  ];

  if (loading) return <PageLoader text="Loading your applications…" />;

  return (
    <div className="container-x py-10 md:py-14">
      <div className="mb-10">
        <span className="overline">Applications</span>
        <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
          Your job{" "}
          <span className="italic text-stone-500">pipeline.</span>
        </h1>
        <p className="mt-3 text-stone-500">{apps.length} application{apps.length !== 1 ? "s" : ""} total</p>
      </div>

      {/* Upcoming interviews */}
      {activeInterviews.length > 0 && (
        <div className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            <CalendarClock size={18} className="text-[#C2410C]" />
            <h2 className="font-display text-2xl">Your interviews</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {activeInterviews.map((iv) => (
              <div key={iv.interview_id} className="rounded-2xl bg-white border border-stone-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-stone-800">{iv.job_title || "Interview"}</p>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border font-mono uppercase tracking-wide ${
                    iv.status === "booked" || iv.status === "rescheduled"
                      ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                      : "bg-amber-50 border-amber-200 text-amber-700"
                  }`}>
                    {iv.status === "invited" ? "Action needed" : "Booked"}
                  </span>
                </div>
                <p className="text-sm text-stone-500">{iv.company_name} · {iv.duration_min}min {iv.mode}</p>
                {iv.scheduled_at && (
                  <p className="text-sm text-stone-600 mt-2 inline-flex items-center gap-1.5">
                    <Clock size={13} className="text-stone-400" />
                    {new Date(iv.scheduled_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                )}
                {iv.status === "invited" && (
                  <p className="text-sm text-amber-700 mt-2">
                    📩 Check your email for the booking link to pick a time.
                  </p>
                )}
                {iv.meeting_url && (
                  <a
                    href={iv.meeting_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#C2410C] text-white hover:bg-orange-700 text-xs font-medium transition-colors"
                  >
                    <Video size={12} /> Join meeting <ExternalLink size={11} />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {apps.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-stone-200 py-20 text-center">
          <Briefcase size={40} className="mx-auto text-stone-300 mb-4"/>
          <p className="text-stone-500 text-lg">No applications yet.</p>
          <Link to="/app/jobs" className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] transition-colors text-sm font-medium">
            Browse jobs <ArrowLeft size={14} className="rotate-180"/>
          </Link>
        </div>
      ) : (
        <div className="overflow-x-auto pb-4">
          <div className="flex gap-4 min-w-max">
            {STAGES.map(stage => {
              const stageApps = apps.filter(a => (a.status || "applied") === stage.id);
              return (
                <div key={stage.id} className="w-64 shrink-0">
                  <div className={`rounded-2xl border-2 ${stage.color} px-4 py-3 mb-3 flex items-center justify-between`}>
                    <span className="font-display text-sm">{stage.label}</span>
                    <span className="text-xs font-mono text-stone-500 bg-white rounded-full px-2 py-0.5">{stageApps.length}</span>
                  </div>
                  <div className="space-y-3">
                    {stageApps.map(app => (
                      <div key={app.application_id || app._id} className="rounded-2xl bg-white border border-stone-200 p-4 shadow-sm hover:shadow-md transition-shadow">
                        <p className="font-display text-base leading-snug">{app.job_title || "Job"}</p>
                        <p className="text-xs text-stone-500 mt-1">{app.company_name || ""}</p>
                        {app.wishire_score_at_apply != null && (
                          <div className="mt-2 inline-flex items-center gap-1 text-xs bg-amber-50 border border-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            <span className="font-mono">{app.wishire_score_at_apply}</span>
                            <span>score at apply</span>
                          </div>
                        )}
                        <p className="text-xs text-stone-400 mt-2 font-mono">
                          {app.created_at ? new Date(app.created_at).toLocaleDateString("en-IN", {day:"numeric", month:"short"}) : ""}
                        </p>
                      </div>
                    ))}
                    {stageApps.length === 0 && (
                      <div className="rounded-2xl border-2 border-dashed border-stone-100 py-8 text-center text-stone-300 text-xs">empty</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function stageStyle(s) {
  if (s === "hired") return "bg-emerald-50 border-emerald-200 text-emerald-800";
  if (s === "rejected") return "bg-stone-100 border-stone-300 text-stone-600";
  if (s === "offer" || s === "interview") return "bg-amber-50 border-amber-200 text-amber-800";
  return "bg-stone-50 border-stone-200 text-stone-700";
}
